import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    enterDuration: { type: Number, default: 260 },
    exitDuration: { type: Number, default: 180 },
  }

  connect() {
    this.isTransitioning = false
    this.lastUrl = window.location.href

    this.boundBeforeVisit = this.beforeVisit.bind(this)
    this.boundBeforeRender = this.beforeRender.bind(this)
    this.boundRender = this.render.bind(this)
    this.boundLoad = this.load.bind(this)
    this.boundBeforeCache = this.beforeCache.bind(this)

    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.addEventListener("turbo:before-render", this.boundBeforeRender)
    document.addEventListener("turbo:render", this.boundRender)
    document.addEventListener("turbo:load", this.boundLoad)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.removeEventListener("turbo:before-render", this.boundBeforeRender)
    document.removeEventListener("turbo:render", this.boundRender)
    document.removeEventListener("turbo:load", this.boundLoad)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.clearTimers()
    this.cleanupClasses()
  }

  // Fires when a link is clicked (before fetch starts)
  // Does NOT fire for back/forward navigation
  beforeVisit(event) {
    if (this.prefersReducedMotion()) return
    if (this.isSamePageUrl(event.detail.url)) return

    this.isTransitioning = true
    this.clearTimers()
    document.body.classList.add("page-exit")
  }

  // Fires before the new body is swapped in
  beforeRender(event) {
    if (this.prefersReducedMotion()) return

    const newBody = event.detail.newBody
    if (!newBody) return

    // For back/forward (restoration), there's no beforeVisit, so no exit animation ran.
    // We still prepare the enter animation.
    if (this.isTransitioning) {
      // Normal navigation: exit animation already running
      newBody.classList.add("page-enter")
    } else if (!this.isSamePageNavigation(event)) {
      // Restoration (back/forward): skip exit, just do enter
      newBody.classList.add("page-enter")
      this.isTransitioning = true
    }
  }

  // Fires after the new body is in the DOM
  render() {
    if (!this.isTransitioning) return
    if (this.prefersReducedMotion()) {
      this.cleanupClasses()
      this.isTransitioning = false
      return
    }

    this.clearTimers()

    // Use double rAF to ensure the browser has painted the initial state
    // before starting the animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.body.classList.remove("page-exit")
        document.body.classList.add("page-enter-active")

        this.enterTimer = window.setTimeout(() => {
          this.cleanupClasses()
          this.isTransitioning = false
        }, this.enterDurationValue)
      })
    })
  }

  // Fires when navigation is fully complete
  load() {
    this.lastUrl = window.location.href

    // Safety cleanup: ensure no stale classes remain
    // This catches edge cases where animation was interrupted
    this.safetyTimer = window.setTimeout(() => {
      if (!this.isTransitioning) {
        this.cleanupClasses()
      }
    }, this.enterDurationValue + this.exitDurationValue + 100)
  }

  // Fires before the page is cached by Turbo
  beforeCache() {
    this.cleanupClasses()
    this.isTransitioning = false
  }

  cleanupClasses() {
    document.body.classList.remove("page-exit", "page-enter", "page-enter-active")
  }

  clearTimers() {
    if (this.exitTimer) window.clearTimeout(this.exitTimer)
    if (this.enterTimer) window.clearTimeout(this.enterTimer)
    if (this.safetyTimer) window.clearTimeout(this.safetyTimer)
  }

  isSamePageUrl(url) {
    if (!url) return false
    try {
      const next = new URL(url, window.location.origin)
      const current = new URL(window.location.href)
      return current.pathname === next.pathname && current.search === next.search
    } catch {
      return false
    }
  }

  isSamePageNavigation(event) {
    const baseUri = event?.detail?.newBody?.baseURI
    return this.isSamePageUrl(baseUri)
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
